import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Reveal } from "@/components/Reveal";
import api, { mediaUrl } from "@/lib/api";
import { VerifiedShop } from "@/components/VerifiedShop";
import { SellerListings } from "@/components/SellerListings";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { PickShopGate } from "@/components/mall/PickShopGate";
import { Crumbs, readyPills } from "@/components/layout/Crumbs";
import { READY_BY_KEY, READY_IN } from "@/lib/nav";
import { PageBanner } from "@/components/mall/PageBanner";

/** One material. The picture is the card; hovering lifts the details out of the bottom edge. */
function MaterialCard({ c, theme, ready }) {
    return (
        <Link to={`/signs/${c.slug}${ready ? `?ready=${ready}` : ""}`} data-testid={`sign-cat-${c.slug}`} style={{ "--c": theme.primary }}
            className="group relative block aspect-[4/5] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-100 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-[color:var(--c)] hover:shadow-[0_24px_40px_-24px_rgba(15,23,42,0.5)]">
            <img src={mediaUrl(c.image_url)} alt={c.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-opacity duration-300 group-hover:from-black/90" />
            <div className="absolute inset-x-0 bottom-0 p-4" style={{ color: "#fff" }}>
                <h3 className="font-display text-xl font-black leading-tight" style={{ color: "#fff" }}>{c.name}</h3>
                <p className="max-h-0 overflow-hidden text-xs leading-snug opacity-0 transition-all duration-300 group-hover:mt-1.5 group-hover:max-h-16 group-hover:opacity-100" style={{ color: "#e4e4e7" }}>
                    {c.description}
                </p>
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold transition-transform duration-300 group-hover:translate-x-1"
                    style={{ color: theme.ink || theme.primary }}>
                    {c.product_count} products <ArrowRight size={12} />
                </span>
            </div>
        </Link>
    );
}

export default function Signs() {
    const [cats, setCats] = useState([]);
    const [meta, setMeta] = useState({});
    const [params] = useSearchParams();
    const ready = READY_BY_KEY[params.get("ready")] ? params.get("ready") : null;
    const { theme: t, store: activeStore } = useMall();
    const { shopSlug } = useShop();

    useEffect(() => {
        if (!shopSlug) return;
        api.get("/signs/categories", { params: { store: shopSlug, ready: ready || undefined } })
            .then(({ data }) => { setCats(data.categories || []); setMeta(data); }).catch(() => { });
    }, [shopSlug, ready]);

    if (!shopSlug) return <Layout><PickShopGate room="signs" title="Which shop makes your sign?" to="/signs" /></Layout>;

    return (
        <Layout>
            <PageBanner room="signs" />
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
                <Reveal>
                    <Crumbs trail={[{ label: "Signs", to: "/signs" }, ...(ready ? [{ label: READY_BY_KEY[ready].label }] : [])]}
                        pills={readyPills("/signs", meta.counts || {}, ready, READY_IN)} />
                    <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: t.ink || t.primary }} data-testid="signs-kicker">
                                Step 1 of 3 · {ready ? `Signs ready in ${READY_BY_KEY[ready].label.toLowerCase()}` : "Most signs are ready in 3 to 5 business days"}
                            </p>
                            <h1 className="mt-1 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="signs-title">Pick a material.</h1>
                            <p className="mt-1 text-base text-zinc-500" data-testid="signs-store-note">
                                {activeStore
                                    ? <>Every sign here is made by <span className="font-semibold text-zinc-900">{activeStore.name}</span>. Hover a card for the details.</>
                                    : "Tap the kind of sign you need. We will walk you through the rest."}
                            </p>
                        </div>
                        <VerifiedShop note="Pro Shop #1 · more shops joining" testid="signs-verified-shop" />
                    </div>
                </Reveal>

                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="sign-materials">
                    {cats.map((c, i) => (
                        <Reveal key={c.slug} delay={i * 0.06}><MaterialCard c={c} theme={t} ready={ready} /></Reveal>
                    ))}
                </div>
            </section>

            <SellerListings
                kind="supply"
                heading="Sign gear and supplies from other shops"
                blurb="Classified ads posted by other shops. You deal with the seller directly."
                sellCta="Sell your material"
                testid="seller-signs"
            />
        </Layout>
    );
}
